import agentWebSocket, { type ConnectionState } from "@app/config/agentWebSocket";
import type { AgentExtraItem } from "@app/config/agentWebSocket";
import { queryClient } from "@app/config/api/api";
import {
  agentSessionDocumentsOptions,
  agentSessionMessagesOptions,
  translateBackendMessages,
  useAgentSessionMessages
} from "@app/queries/agent";
import { useAuth0 } from "@auth0/auth0-react";
import type { AgentServerMessage, ExperimentId, ToolApprovalDecision, ToolApprovalRequest } from "@globals";
import type { ChatMessage, CommittedDocuments } from "@lib/agent/types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

let messageIdCounter = 0;
function nextId(): string {
  return `msg-${++messageIdCounter}`;
}

// Index of the last assistant message, or -1. The chunk/error handlers target the streaming
// bubble by role + position rather than object identity (which would break if the array were
// ever cloned). Manual loop instead of Array.findLastIndex — the app tsconfig targets lib ES2022.
function lastAssistantIndex(messages: ChatMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === "assistant") {
      return i;
    }
  }
  return -1;
}

export function useAgentChat(experimentId: ExperimentId, teamId: string | null) {
  const [isStreaming, setIsStreaming] = useState(false);
  // Optimistic flag set the instant the user clicks Stop. Backend cancellation is cooperative —
  // the worker only notices the cancel flag at the next stream message boundary, which can take
  // several seconds — so without this the Stop button gives no feedback and gets spammed. Cleared
  // wherever the turn settles (turn.done, error, reconnect) or a fresh turn starts.
  const [isStopping, setIsStopping] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(agentWebSocket.state);
  const isReady = connectionState === "connected";
  // Messages belonging to the current live turn (≤ 2 items: user prompt + streaming assistant).
  // Cleared on agent.turn.done and reconnect.
  const [liveMessages, setLiveMessages] = useState<ChatMessage[]>([]);
  // Extras from the most recent agent.extras message — streamed before agent.turn.done,
  // persisted in memory so suggestions remain visible after the turn completes.
  // Lost on page refresh / reconnect. Cleared on new send or clearLocal.
  const [liveExtras, setLiveExtras] = useState<AgentExtraItem[] | undefined>(undefined);
  // agent_session_id from the most recent turn; falls back to persisted historyData.
  const [liveAgentSessionId, setLiveAgentSessionId] = useState<string | null>(null);
  // Gated tool calls awaiting the user's decision. The turn stays parked server-side until
  // answered, so this drives a blocking card rather than a dismissible notice.
  const [pendingApprovals, setPendingApprovals] = useState<ToolApprovalRequest[]>([]);

  const { getAccessTokenSilently } = useAuth0();

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: historyError,
    refetch: refetchHistory
  } = useAgentSessionMessages(experimentId);

  // allMessages is the single derived view: persisted history + any live-turn messages.
  // historyData.messages only contains committed messages — the current assistant reply is not
  // written to the DB until AgentCore finishes streaming it, so in-progress chunk content is
  // never present in history. A page refresh mid-turn restores the spinner but not the partial
  // text; new chunks stream in normally from wherever the agent currently is in its turn.
  const allMessages = useMemo(() => {
    const serverMessages = translateBackendMessages(historyData?.messages ?? []);
    return [...serverMessages, ...liveMessages];
  }, [historyData, liveMessages]);

  const agentSessionId = liveAgentSessionId ?? historyData?.agent_session_id ?? null;
  const committedMessageCount = historyData?.messages?.length ?? 0;

  const clearLiveState = useCallback(() => {
    setLiveMessages([]);
    setLiveExtras(undefined);
    setLiveAgentSessionId(null);
  }, []);

  // Counts turns started from this tab. The turn.done handler captures it before its history
  // refetch so a late `.then` can tell whether the turn it belongs to is still the current one.
  const turnGenerationRef = useRef(0);

  const clearLocal = useCallback(() => {
    clearLiveState();
    // The card outlives the turn that raised it, so unlike the live state above it is not reset
    // by anything else here. Leaving it up after "Clear conversation" would offer to launch a run
    // for a session that no longer exists; the connect effect below puts back whatever the server
    // still has.
    setPendingApprovals([]);
    setIsStreaming(false);
    setIsStopping(false);
  }, [clearLiveState]);

  // Connect on mount, reconnect when the experiment's team changes
  useEffect(() => {
    void agentWebSocket.connect(() => getAccessTokenSilently(), teamId);
    return () => agentWebSocket.close();
  }, [teamId, getAccessTokenSilently]);

  // Track connection state
  useEffect(() => agentWebSocket.onStateChange(setConnectionState), []);

  // On (re)connect: reset all local state and refetch server history. Using clearLocal (not
  // clearLiveState) so isStreaming is reset too — a turn that finished while disconnected never
  // delivers agent.turn.done, so without this the spinner would stay up forever. If the turn is
  // still running, the is_turn_in_progress effect below immediately re-sets isStreaming to true.
  // The card is restored from the same fetch. The request arrives over the WS when the turn
  // pauses, so a browser that was not listening then — reloaded, freshly opened, or navigated away
  // and back — would otherwise show a conversation that looks finished while the runtime is still
  // parked. Restoring here rather than from an effect watching the query data is what keeps a
  // stale read out of it: this fetch is issued after the connection is up, whereas the refetch
  // `turn.done` starts can have been in flight since before the user answered, and would put an
  // answered card back with interrupt ids the runtime no longer holds.
  useEffect(() => {
    if (!isReady) {
      return;
    }
    clearLocal();
    void queryClient
      .fetchQuery(agentSessionMessagesOptions(experimentId))
      .then(data => setPendingApprovals(data.pending_tool_approvals))
      .catch(() => {
        // Leave the card down; the history query surfaces the failure on its own.
      });
  }, [isReady, clearLocal, experimentId]);

  // When the server reports a turn still running (reconnected mid-turn from a previous session),
  // show the streaming spinner with a pending assistant bubble until agent.turn.done arrives.
  // Declared after the isReady effect so React runs clearLocal() first in the same render cycle,
  // then this effect restores isStreaming. isReady is a dep so reconnect re-triggers this even
  // when is_turn_in_progress stays true (its value would not have changed otherwise).
  useEffect(() => {
    if (!isReady || !historyData?.is_turn_in_progress) {
      return;
    }
    setIsStreaming(true);
    setLiveMessages(prev => {
      if (prev.some(m => m.role === "assistant")) {
        return prev;
      }
      return [{ id: nextId(), role: "assistant", content: "" }];
    });
  }, [isReady, historyData?.is_turn_in_progress]);

  // Handle server messages
  useEffect(() => {
    const handleMessage = (message: AgentServerMessage) => {
      switch (message.action) {
        case "agent.response.chunk": {
          // Functional update: prev always reflects latest state, no external buffer needed.
          setLiveMessages(prev => {
            const idx = lastAssistantIndex(prev);
            if (idx === -1) {
              return prev;
            }
            return prev.map((m, i) => (i === idx ? { ...m, content: m.content + message.payload.message } : m));
          });
          break;
        }
        case "agent.tool_use": {
          // Any tool call may be followed by more streamed text; append a paragraph break so
          // subsequent chunks start on a new line instead of running onto preceding text.
          setLiveMessages(prev => {
            const idx = lastAssistantIndex(prev);
            if (idx === -1) {
              return prev;
            }
            return prev.map((m, i) => (i === idx ? { ...m, content: m.content + "\n\n" } : m));
          });
          break;
        }
        case "agent.turn.started": {
          // A turn this client did not start (the agent raising a finished research report)
          // would otherwise stream into nothing: chunks are dropped when no assistant bubble is
          // waiting. Scoped by experiment because the stream is broadcast on the user's group.
          if (message.payload.experiment_id !== experimentId) {
            break;
          }
          // Same bookkeeping as send(): a refetch still in flight from the previous turn must
          // not clear the bubble this one is about to stream into.
          turnGenerationRef.current += 1;
          setIsStreaming(true);
          // Chips belong to the turn that offered them, and this one replaces it: left up they
          // would sit under the thinking indicator, and clicking one would dispatch nothing
          // because a prompt is refused mid-turn. Every other turn start clears them too, so
          // this is a no-op for a turn the client started itself.
          setLiveExtras(undefined);
          setLiveMessages(prev => {
            // A turn this client started already put its bubble up as the last message.
            const last = prev.at(-1);
            if (last?.role === "assistant" && last.content === "") {
              return prev;
            }
            // An error is the one thing here that exists nowhere else — `agent.error` keeps it in
            // the overlay precisely because error text is never written to history. Add to it
            // rather than over it, or the user loses what went wrong with no way back to it.
            if (last?.role === "error") {
              return [...prev, { id: nextId(), role: "assistant", content: "" }];
            }
            // Otherwise replace, for the same reason `respondToApproval` replaces: the overlay
            // still holds the previous turn, which `turn.done` is writing into history, and the
            // generation bump above stops that refetch clearing it. Appending would leave the
            // finished turn rendered twice.
            return [{ id: nextId(), role: "assistant", content: "" }];
          });
          break;
        }
        case "agent.tool_approval_required": {
          // Scoped like agent.turn.done: the stream is broadcast on the user's group, so a turn
          // left running on another experiment pauses into this chat too. Answering it here would
          // resume under this experiment's id with interrupt ids its session never raised.
          if (message.payload.experiment_id !== experimentId) {
            break;
          }
          // The runtime finished this turn when it paused, so streaming has stopped: swap the
          // spinner for the card, otherwise the user waits on a turn that will never advance.
          setPendingApprovals(message.payload.requests);
          setIsStreaming(false);
          break;
        }
        case "agent.extras": {
          setLiveExtras(message.payload.items);
          break;
        }
        case "agent.turn.done": {
          if (message.payload.experiment_id !== experimentId) {
            break;
          }
          // Clear liveMessages only once the refetch succeeds: fetchQuery resolves after the
          // cache is written, so the committed turn is renderable from server history before the
          // overlay disappears (no flash). On failure we deliberately keep liveMessages — server
          // history lacks the new turn, so there is nothing to dedupe against and clearing would
          // just blank the content the user watched stream in. The next send/reconnect resets it.
          // Deliberately does NOT clear pendingApprovals: pausing for approval *ends* the turn,
          // so this arrives immediately after `agent.tool_approval_required` and clearing here
          // removed the card before it could ever render. It is cleared when answered and when
          // the user sends something else instead. Stop needs no clear of its own — it only runs
          // while a turn is streaming, and a card is only up once the turn has ended.
          setLiveAgentSessionId(message.payload.agent_session_id ?? null);
          setIsStreaming(false);
          setIsStopping(false);
          // The refetch is async, and the next turn can start before it resolves — pausing for
          // approval makes that ordinary, since the card arrives with this event and invites an
          // immediate answer. Clearing then would blank the bubble the new turn had already put
          // up, and chunks arriving after would have no assistant message to append to. So clear
          // only if this is still the turn that finished.
          const settledTurn = turnGenerationRef.current;
          void queryClient
            .fetchQuery(agentSessionMessagesOptions(experimentId))
            .then(() => {
              if (turnGenerationRef.current === settledTurn) {
                setLiveMessages([]);
              }
            })
            .catch(() => {
              // Keep the streamed turn visible; do not blank it on a transient refetch error.
            });
          break;
        }
        case "agent.error": {
          // Intentionally does NOT refetch committed history. The error text is client-only
          // (never persisted), so it lives solely in this overlay; the user prompt, however, was
          // already committed by AgentCore. Calling fetchQuery here would pull that committed
          // prompt into historyData while the overlay still holds the same prompt, re-creating the
          // duplicate bubble that refetchOnWindowFocus/Reconnect/Mount: false was added to prevent.
          // The errored exchange therefore stays in the overlay until the next send replaces it.
          // An error that means the turn never started leaves the runtime parked on its approval
          // request; the server restates the card in its own message rather than this handler
          // guessing which errors those are.
          setLiveMessages(prev => {
            // The error gets its own bubble rather than being written into the assistant's. Filling
            // the assistant bubble only worked while it was empty, so any turn that had already put
            // something there dropped the error text entirely — one tool call is enough, since
            // `agent.tool_use` writes a paragraph break into that same bubble.
            const idx = lastAssistantIndex(prev);
            const withoutEmptyReply = idx !== -1 && !prev[idx].content.trim() ? prev.filter((_, i) => i !== idx) : prev;
            return [...withoutEmptyReply, { id: nextId(), role: "error", content: message.payload.message }];
          });
          setIsStreaming(false);
          setIsStopping(false);
          break;
        }
      }
    };

    return agentWebSocket.onServerMessage(handleMessage);
  }, [experimentId]);

  const send = useCallback(
    // Returns whether the prompt was actually dispatched. Callers that commit side effects before
    // sending (e.g. uploading documents) need this to avoid clearing their draft when the frame
    // was dropped on a not-ready socket.
    (content: string, attachments?: { filenames?: string[]; reports?: { id: string; title: string }[] }): boolean => {
      if (!content.trim() || isStreaming || isHistoryLoading || historyError) {
        return false;
      }
      // The input is gated on `isReady` at the component layer; this guard catches the
      // race where the WS drops between render and send so the frame isn't silently
      // discarded on a dead socket. The user retries once the auto-reconnect catches up.
      if (!isReady) {
        return false;
      }

      const filenames = attachments?.filenames ?? [];
      const reports = attachments?.reports ?? [];
      const chipAttachments = [
        ...filenames.map(name => ({ name, kind: "document" as const })),
        ...reports.map(r => ({ name: r.title, kind: "report" as const }))
      ];

      const userMessage: ChatMessage = {
        id: nextId(),
        role: "user",
        content,
        attachments: chipAttachments.length > 0 ? chipAttachments : undefined
      };
      const assistantMessage: ChatMessage = { id: nextId(), role: "assistant", content: "" };

      turnGenerationRef.current += 1;
      setLiveMessages([userMessage, assistantMessage]);
      setLiveExtras(undefined);
      // Sending something else instead of answering abandons the approval, which is what the
      // runtime does with the interrupt on its side too. If this prompt turns out not to start a
      // turn, the server restates the card, since the runtime is then still parked on it.
      setPendingApprovals([]);
      setIsStreaming(true);
      setIsStopping(false);

      // Documents are uploaded out-of-band via the REST endpoint and attached to the session; the
      // server hands the agent the session's full document set each turn, so the prompt frame
      // carries no document handles (`names` is only for optimistic chips). Research reports are
      // message-scoped: their ids travel with this prompt so Django can resolve bodies for this turn.
      agentWebSocket.send({
        action: "agent.prompt",
        payload: {
          message: content,
          experiment_id: experimentId,
          report_ids: reports.map(r => r.id)
        }
      });
      return true;
    },
    [isStreaming, isHistoryLoading, historyError, experimentId, isReady]
  );

  const respondToApproval = useCallback(
    (decisions: ToolApprovalDecision[]) => {
      if (decisions.length === 0 || !isReady) {
        return;
      }
      // Clear before sending: the card is answered exactly once, and leaving it up while the
      // resumed turn streams is what produced the duplicate-box behaviour previously. If the
      // resume fails to start a turn, the server restates the card rather than this tab holding a
      // copy back in case it needs undoing.
      setPendingApprovals([]);
      turnGenerationRef.current += 1;
      setIsStreaming(true);
      // Replaced, not appended to, exactly as a typed prompt does. What is in the overlay belongs
      // to the paused turn, which is over and is being written into history by the refetch that
      // `turn.done` started. Keeping it would leave the same turn rendered twice once that
      // refetch lands — and the generation guard above deliberately stops the refetch clearing
      // the overlay, so nothing else would remove it.
      setLiveMessages([{ id: nextId(), role: "assistant", content: "" }]);
      // Chips belong to the paused turn and act on click, so leaving them up would let one
      // dispatch a prompt against the turn that replaced it.
      setLiveExtras(undefined);
      agentWebSocket.send({
        action: "agent.tool_approval_response",
        payload: { experiment_id: experimentId, decisions }
      });
    },
    [experimentId, isReady]
  );

  const stop = useCallback(() => {
    if (!isStreaming || isStopping) {
      return;
    }
    agentWebSocket.send({
      action: "agent.stop",
      payload: { experiment_id: experimentId }
    });
    // Remove the partial assistant bubble and stale extras immediately;
    // agent.turn.done will arrive shortly via the channel layer and trigger the server refetch.
    setLiveMessages(prev => prev.filter(m => m.role !== "assistant"));
    setLiveExtras(undefined);
    setIsStopping(true);
  }, [isStreaming, isStopping, experimentId]);

  // A 403 (feature flag) or 5xx must not propagate as a thrown error; an unknown count only
  // relaxes the local picker hint, and the upload endpoint stays authoritative on the cap.
  const documentsQuery = useQuery({
    ...agentSessionDocumentsOptions(experimentId),
    enabled: Boolean(experimentId),
    throwOnError: false
  });
  const committedDocuments: CommittedDocuments = {
    count: documentsQuery.data?.documents.length ?? 0,
    totalBytes: documentsQuery.data?.documents.reduce((sum, d) => sum + d.size_bytes, 0) ?? 0
  };

  return {
    messages: allMessages,
    liveExtras,
    isStreaming,
    isStopping,
    connectionState,
    send,
    stop,
    pendingApprovals,
    respondToApproval,
    isHistoryLoading,
    historyError,
    refetchHistory,
    clearLocal,
    hasRestoredHistory: translateBackendMessages(historyData?.messages ?? []).length > 0,
    agentSessionId,
    committedMessageCount,
    committedDocuments
  };
}

export type AgentChatSend = ReturnType<typeof useAgentChat>["send"];
